import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { BentoToolbarItem } from '@bento/bento-ng';
import { getBindings } from '@onviobr/services/session';
import { registerTree, TreeItem, TreeStore } from '@onviobr/ui-elements/bm-tree';
import { from, of, zip, Observable, forkJoin } from 'rxjs';
import { groupBy, mergeMap, toArray } from 'rxjs/operators';
import { ProfileReportsService } from '../../services/profile-reports.service';
import { ReportProfileSelected, ReportProfileTreeType } from '../../models/report';

@Component({
	selector: 'app-firm-management-report-tree',
	templateUrl: './firm-management-report-tree.component.html',
	styleUrls: ['./firm-management-report-tree.component.css']
})
export class FirmManagementReportTreeComponent implements OnInit {

	companyId: string;
	contactId: string;
	getBindings = getBindings;
	employeeId: string;
	reports: any[] = [];
	staffReportsData: any[] = [];
	firmReportsData: any[] = [];



	staffId: string[] = [];
	firmId: string[] = [];
	standardReport = [{
		id: 'standard',
		name: 'Reports',
		subItems: [{
			id: {} as string,
			name: {} as string,
			subItems: {} as TreeItem
		}]
	}];

	groupedStandardReport = [{
		id: {} as string,
		name: {} as string,
		isParent: {} as boolean,
		subItems: [] as TreeItem[]
	}];

	staffReport = [{
		id: 'staff',
		name: 'Staff Report Profiles',
		subItems: [] as TreeItem[]
	}];

	firmReport = [{
		id: 'firm',
		name: 'Firm Report Profiles',
		subItems: [] as TreeItem[]
	}];

	standardStore: TreeStore = new TreeStore(false);
	staffStore: TreeStore = new TreeStore(false);
	firmStore: TreeStore = new TreeStore(false);

	toolBarButtons: BentoToolbarItem[] = [
		{
			label: 'Expand All',
			icon: 'bento-icon-expand',
			action: () => this.expandOrCollapse(true)
		},
		{
			label: 'Collapse All',
			icon: 'bento-icon-collapse',
			action: () => this.expandOrCollapse(false)
		}
	];

	// read selected checkbox
	@Output() readStaffIds = new EventEmitter<string[]>();
	@Output() readFirmIds = new EventEmitter<string[]>();
	@Output() readStandardIds = new EventEmitter<string[]>();
	@Output() selectedProfile = new EventEmitter<ReportProfileSelected>();

	constructor(public profileReportsService: ProfileReportsService) {
		registerTree();
	}

	async ngOnInit() {
		const bindings = await this.getBindings();
		this.companyId = bindings.ApplicationConfiguration.CompanyId;
		this.contactId = bindings.ApplicationConfiguration.ContactId;

		this.standardReports();
		this.staffEmployeeReport();
		this.firmReports();
	}

	standardReports(): void {
		this.profileReportsService.refreshStandardProfile.asObservable().subscribe(() => {
			this.loadReportsTree(this.standardStore, this.standardReport, 'standard');
		});
	}

	staffEmployeeReport(): void {
		if (!this.employeeId) {
			this.profileReportsService.readEmployeeId(this.companyId, this.contactId)
				.then(data => {
					this.employeeId = data;
					this.staffReports();
				});

		} else {
			// if employeeId already exist it will not call api to fetch employeeId
			this.staffReports();
		}
	}

	staffReports(): void {
		this.profileReportsService.refreshStaffProfile.asObservable().subscribe(() => {
			this.loadReportsTree(this.staffStore, this.staffReport, 'staff');
		});
	}

	firmReports(): void {
		this.profileReportsService.refreshFirmProfile.asObservable().subscribe(() => {
			this.loadReportsTree(this.firmStore, this.firmReport, 'firm');
		});
	}

	/** Used to call reports tree's api and make recursive call if it has more than 1 pages */
	loadReportsTree(store: TreeStore, treeObject, treeType: 'standard' | 'staff' | 'firm') {
		this.requestForStaffFirm(treeType, 1).subscribe(firstResult => {

			let itemIndex = 1;
			const requests: Observable<any>[] = [];

			// let pageNumber = 2 : we set 2 because for 1st time it getting from main method.
			for (let pageNumber = 2; pageNumber <= firstResult.totalPages; pageNumber++) {
				// to find next index for next request.
				itemIndex = itemIndex + firstResult.itemsPerPage;
				requests.push(this.requestForStaffFirm(treeType, itemIndex));
			}

			if (requests.length === 0) {
				// this trigger only when if we don't have next page for reading. so it will set firstreult to tree
				this.mapPagingRowsToStore(store, treeObject, [firstResult], treeType);
			}

			forkJoin(requests).subscribe(allResults => {
				allResults.push(firstResult);
				this.mapPagingRowsToStore(store, treeObject, allResults, treeType);
			});
		});
	}

	/** this return which service need to call based on parameter */
	requestForStaffFirm(treeType: string, index: number) {
		switch (treeType) {
		case 'standard': return this.profileReportsService.reportDefinition(this.companyId, index);
		case 'staff': return this.profileReportsService.staffProfile(this.companyId, this.employeeId, index);
		case 'firm': return this.profileReportsService.firmProfile(this.companyId, index);
		}
	}

	mapPagingRowsToStore(store: TreeStore, staffOrFirmRow: any[], results: any[], treeType: string) {
		let data = [];
		results.forEach(element => {
			data = data.concat(element.items);
		});
		if (treeType === 'standard') {
			this.mapStandardReportWithGrouping(data);
		} else {
			staffOrFirmRow[0].subItems = this.mapStaffAndFirmProfile(data);
			store.setStore(staffOrFirmRow as any);
		}
	}

	mapStaffAndFirmProfile(rows: Array<any>) {
		return rows.map(x => ({
			id: x.id,
			name: x.name,
			subItems: x.reportProfileSelections.map(child => ({
				id: child.id,
				name: child.name
			})),
			isParent: true
		})).sort((a, b) => a.name.localeCompare(b.name));
	}

	// to group the standard reports according to reportCategoryType
	mapStandardReportWithGrouping(data: any): void {
		this.standardReport[0].subItems = data.map(x => ({
			id: x.reportCategoryType.id, name: x.reportCategoryType.name,
			subItems: {
				id: x.id,
				name: x.name
			}
		}));
		this.groupedStandardReport = [];
		from(this.standardReport[0].subItems).pipe(
			groupBy(r => r.id),
			mergeMap(report => zip(of(report.key), report.pipe(toArray())))
		).subscribe(val => {
			let name: string;
			val[1].filter(v => {
				name = v.name;
				delete v.id; delete v.name;
			});
			this.groupedStandardReport.push({
				id: val[0],
				name,
				isParent: true,
				subItems: val[1].map(v => ({ id: v.subItems.id, name: v.subItems.name }))
			});
		});
		this.standardReport[0].subItems = this.groupedStandardReport as any;
		this.standardStore.setStore(this.standardReport as any);
	}

	staffChecked() {
		const ids = this.checkedProfileId(this.staffStore);
		this.readStaffIds.emit(ids);
	}

	firmChecked() {
		const ids = this.checkedProfileId(this.firmStore);
		this.readFirmIds.emit(ids);
	}
	standardChecked() {
		const ids = this.checkedProfileId(this.standardStore);
		this.readStandardIds.emit(ids);
	}

	checkedProfileId(treeStore: TreeStore): string[] {
		const checkedItems = treeStore.getChecked() as Array<any>;
		let ids: string[] = checkedItems.filter(x => x.isParent).map(m => m.id);
		ids = ids.filter((elem, index, self) => index === self.indexOf(elem));
		return ids;
	}

	standardSelected(event: CustomEvent<Array<TreeItem>>): void {
		this.setSelectedProfile(event, 'Standard');
	}

	firmSelected(event: CustomEvent<Array<TreeItem>>): void {
		this.setSelectedProfile(event, 'Firm');

	}

	staffSelected(event: CustomEvent<Array<TreeItem>>): void {
		this.setSelectedProfile(event, 'Staff');
	}

	setSelectedProfile(event: CustomEvent<Array<TreeItem>>, type: ReportProfileTreeType) {
		const detail = event.detail[0];
		if (detail && detail['isParent']) {
			this.selectedProfile.emit({ id: detail.id, profileType: type, detail });
		}
	}

	onItemExpanded() {
		// alert('onItemExpanded');
	}

	expandOrCollapse(isVisible: boolean): void {
		this.standardStore.setAll({ visible: isVisible });
		this.firmStore.setAll({ visible: isVisible });
		this.staffStore.setAll({ visible: isVisible });
	}
}
